import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TranslationProvider } from "./contexts/TranslationContext";
import Login from "./components/auth/Login";
import Dashboard from "./components/dashboard/Dashboard";
import FormBuilder from "./components/forms/FormBuilder";
import FormList from "./components/forms/FormList";
import FormDetail from "./components/forms/FormDetail";
import UserProfile from "./components/users/UserProfile";
import UserManagement from "./components/users/UserManagement";
import WooCommerceOrders from "./components/woocommerce/WooCommerceOrders";
import Layout from "./components/layout/Layout";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8001";

const queryClient = new QueryClient();

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" /> : <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== "admin") {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

function App() {
  // Check if this is a public form route and redirect to backend
  React.useEffect(() => {
    const path = window.location.pathname;
    const query = window.location.search;
    if (
      path.startsWith("/form/") &&
      path !== "/forms" &&
      !path.startsWith("/forms/")
    ) {
      // This is a public form route, redirect to backend
      window.location.href = `${API_BASE_URL}${path}`;
    } else if (path.startsWith("/form/submit")) {
      // This is a public form route, redirect to backend
      window.location.href = `${API_BASE_URL}${path}${query}`;
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TranslationProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <Routes>
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                {/* Registration disabled */}
                <Route path="/register" element={<Navigate to="/login" />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/forms"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FormList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/forms/new"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FormBuilder />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/forms/:id"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FormDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/forms/:id/edit"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FormBuilder />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <UserProfile />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <AdminRoute>
                      <Layout>
                        <UserManagement />
                      </Layout>
                    </AdminRoute>
                  }
                />
                <Route
                  path="/woocommerce/orders"
                  element={
                    <AdminRoute>
                      <Layout>
                        <WooCommerceOrders />
                      </Layout>
                    </AdminRoute>
                  }
                />
                <Route path="/" element={<Navigate to="/dashboard" />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </TranslationProvider>
    </QueryClientProvider>
  );
}

export default App;
